import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Lightbulb, Tag } from "lucide-react";
import SEO from "@/components/SEO";
import SiteFooter from "@/components/SiteFooter";
import { ArticleOutline, ArticleOutlineMobile, buildOutline, useOutlineSpy } from "@/components/ArticleOutline";
import { PUBLIC_SITE_URL } from "@/data/seo";
import { getMedicalWebPageLD, getBreadcrumbLD } from "@/data/structuredData";
import { enquiryPath, kbEnquiryParams } from "@/data/enquiry";
import { relatedSlugs } from "@/data/kbRelated";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArticleDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) {
        setError("No article found");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/articles/${slug}`);
        
        if (!res.ok) {
          setError(res.status === 404 ? "Article not found" : "Failed to load");
          setLoading(false);
          return;
        }
        
        const data = await res.json();
        setArticle(data);
        setLoading(false);

        // Fetch curated related guides (same treatment family); fall back to API related
        const curated = relatedSlugs(slug);
        if (curated.length) {
          fetch(`${API_URL}/api/articles?limit=100`)
            .then(r => r.ok ? r.json() : { articles: [] })
            .then(d => {
              const bySlug = {};
              (d.articles || []).forEach(a => { bySlug[a.slug] = a; });
              const picked = curated.map(s => bySlug[s]).filter(Boolean);
              if (picked.length) setRelated(picked);
              else {
                fetch(`${API_URL}/api/articles/${slug}/related`)
                  .then(r => r.ok ? r.json() : [])
                  .then(rel => setRelated(rel || []))
                  .catch(() => {});
              }
            })
            .catch(() => {});
        } else {
          fetch(`${API_URL}/api/articles/${slug}/related`)
            .then(r => r.ok ? r.json() : [])
            .then(d => setRelated(d || []))
            .catch(() => {});
        }

        // Track read in background
        fetch(`${API_URL}/api/articles/${slug}/track-read`, { method: "POST" })
          .catch(() => {});
          
      } catch (err) {
        setError("Failed to load article");
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  // Parse body and inject Harvard citations as superscript numbers
  const processBodyWithCitations = (htmlBody, references) => {
    if (!references || references.length === 0) return htmlBody;

    let processedBody = htmlBody;
    
    // Replace citation markers [1], [2] etc with superscript links
    references.forEach((ref, idx) => {
      const citationNumber = idx + 1;
      const regex = new RegExp(`\\[${citationNumber}\\]`, 'g');
      processedBody = processedBody.replace(
        regex,
        `<sup class="citation-link"><a href="#ref-${citationNumber}" class="text-teal-600 hover:text-teal-800 font-semibold">[${citationNumber}]</a></sup>`
      );
    });

    return processedBody;
  };

  // Format reference in Harvard style
  const formatHarvardReference = (ref) => {
    const parts = [];

    // New shape from the Content Creator: a pre-formatted Harvard citation string
    // (sanitised server-side to <em>/<strong> only) plus an optional URL.
    if (ref.citation) {
      parts.push(<span key="citation" dangerouslySetInnerHTML={{ __html: ref.citation }} />);
      if (ref.url) {
        parts.push(
          <span key="url"> Available at: <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline break-all">{ref.url}</a></span>
        );
      }
      return parts;
    }

    // Legacy shape: Authors (Year) Title. Journal. Volume(Issue), pages. DOI/URL

    if (ref.authors) {
      parts.push(<strong key="authors">{ref.authors}</strong>);
    }
    
    if (ref.year) {
      parts.push(<span key="year"> ({ref.year})</span>);
    }
    
    if (ref.title) {
      parts.push(<em key="title"> {ref.title}.</em>);
    }
    
    if (ref.journal) {
      parts.push(<span key="journal"> <em>{ref.journal}</em></span>);
    }
    
    if (ref.volume) {
      parts.push(<span key="volume">, {ref.volume}</span>);
      if (ref.issue) {
        parts.push(<span key="issue">({ref.issue})</span>);
      }
    }
    
    if (ref.pages) {
      parts.push(<span key="pages">, pp. {ref.pages}</span>);
    }
    
    if (ref.doi) {
      parts.push(<span key="doi">. DOI: <a href={`https://doi.org/${ref.doi}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">{ref.doi}</a></span>);
    } else if (ref.url) {
      parts.push(<span key="url">. Available at: <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline break-all">{ref.url}</a></span>);
    }
    
    if (ref.accessed) {
      parts.push(<span key="accessed" className="text-gray-500"> [Accessed {ref.accessed}]</span>);
    }

    return parts;
  };

  const trackShare = async (channel) => {
    await fetch(`${API_URL}/api/articles/${slug}/track-share?channel=${channel}`, {
      method: "POST"
    });
  };

  const handleShare = (channel) => {
    const url = `https://restore.dental/knowledge-base/${slug}`;
    const title = article?.title || "";

    trackShare(channel);

    switch (channel) {
      case "whatsapp":
        window.open(`https://wa.me/?text=${encodeURIComponent(title + " " + url)}`, "_blank");
        break;
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank");
        break;
      case "x":
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`, "_blank");
        break;
      case "linkedin":
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank");
        break;
      case "copy":
        navigator.clipboard.writeText(url);
        alert("Link copied to clipboard!");
        break;
      default:
        break;
    }
  };

  const { html: bodyHtml, outline } = useMemo(
    () => buildOutline(processBodyWithCitations(article?.body || "", article?.references)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [article?.body, article?.references]
  );
  const { activeId, goTo } = useOutlineSpy(outline);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Article Not Found</h2>
          <p className="text-gray-600 mb-6">{error || "The article you're looking for doesn't exist."}</p>
          <Link
            to="/knowledge-base"
            className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition"
          >
            Back to Knowledge Base
          </Link>
        </div>
      </div>
    );
  }

  const enq = kbEnquiryParams(article);
  const enquireTo = enquiryPath(enq.slug, enq.about);
  const takeaways = article.keyTakeaways || article.takeaways || [];
  const rawHero = article.hero_image_url || article.hero_image || article.heroImage || "";
  const heroImg = rawHero.startsWith("/") ? `${API_URL}${rawHero}` : rawHero;
  const tags = (article.tags || []).filter(Boolean);

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.meta_keywords || article.tags}
        type="article"
        path={`/knowledge-base/${encodeURIComponent(article.slug || slug)}`}
        image={`/api/articles/${encodeURIComponent(article.slug || slug)}/share-card.jpg`}
        publishedTime={article.publishedAt || article.created_at}
        modifiedTime={article.updated_at || article.publishedAt || article.created_at}
        author={article.author_name || 'Dr. Kartik Patel'}
        structuredData={[
          getMedicalWebPageLD({
            headline: article.title,
            description: article.excerpt,
            url: `${PUBLIC_SITE_URL}/knowledge-base/${encodeURIComponent(article.slug || slug)}`,
            datePublished: article.publishedAt || article.created_at,
            dateModified: article.updated_at || article.publishedAt || article.created_at,
            image: article.hero_image || article.hero_image_url || `${PUBLIC_SITE_URL}/og-image.jpg`,
            keywords: article.tags,
            articleSection: article.category,
            wordCount: article.body ? article.body.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length : undefined,
          }),
          getBreadcrumbLD([
            { name: 'Home', url: `${PUBLIC_SITE_URL}/` },
            { name: 'Knowledge Base', url: `${PUBLIC_SITE_URL}/knowledge-base` },
            { name: article.title, url: `${PUBLIC_SITE_URL}/knowledge-base/${encodeURIComponent(article.slug || slug)}` },
          ]),
        ]}
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section - Improved */}
        <div className="bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 text-white py-20">
          <div className="max-w-5xl mx-auto px-4">
            <Link
              to="/knowledge-base"
              className="inline-flex items-center text-white/80 hover:text-white mb-8 transition font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Knowledge Base
            </Link>
            
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="inline-block px-4 py-1.5 bg-teal-600 rounded-full text-sm font-medium">
                {article.category}
              </span>
              {article.is_evidence_based && (
                <span className="inline-block px-4 py-1.5 bg-green-600 rounded-full text-sm font-medium flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Evidence-Based
                </span>
              )}
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">{article.title}</h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed max-w-4xl">{article.excerpt}</p>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/70 border-t border-white/20 pt-6">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">{article.author_name}</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                <span>{new Date(article.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>{article.readMinutes} min read</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                <span>{article.reads} reads</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main content — editorial two-column layout */}
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 md:py-14 lg:px-8">
          {heroImg && (
            <figure className="mb-10">
              <img
                src={heroImg}
                alt={article.hero_image_alt || article.title}
                className="h-64 w-full rounded-2xl object-cover md:h-96"
              />
              {article.hero_image_caption && (
                <figcaption className="mt-3 text-sm italic text-stone-500">
                  {article.hero_image_caption}
                </figcaption>
              )}
            </figure>
          )}

          {/* Key takeaways */}
          {takeaways.length > 0 && (
            <div
              className="mb-7 rounded-r-xl border-l-4 border-[#006A71] bg-[#006A71]/[0.055] p-5 md:p-6"
              data-testid="article-takeaways"
            >
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#006A71]">
                <Lightbulb className="h-4 w-4" aria-hidden="true" />
                In this article, you'll learn
              </p>
              <ul className="mt-4 space-y-2.5">
                {takeaways.map((t, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 leading-relaxed text-stone-800"
                    data-testid={`article-takeaway-${idx}`}
                  >
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#006A71]" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Keywords */}
          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="article-keyword-tags">
              {tags.map((tag, idx) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-600"
                  data-testid={`article-tag-chip-${idx}`}
                >
                  <Tag className="h-3 w-3 text-stone-400" aria-hidden="true" />
                  {tag}
                </span>
              ))}
            </div>
          )}

          <hr className="my-8 border-stone-200 md:my-10" />

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
            {/* Outline sidebar */}
            <aside className="hidden lg:col-span-3 lg:block">
              <div className="sticky top-28 max-h-[calc(100vh-9rem)] overflow-y-auto pr-2">
                <ArticleOutline
                  items={outline}
                  activeId={activeId}
                  goTo={goTo}
                  footer={
                    <div className="mt-6 space-y-2 border-t border-stone-200 pt-5 text-xs text-stone-500">
                      <p>
                        Updated{" "}
                        {new Date(article.updated_at || article.publishedAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                      {article.references?.length > 0 && <p>{article.references.length} sources cited</p>}
                      <p>Written for patients, reviewed by Dr Kartik Patel</p>
                    </div>
                  }
                />
              </div>
            </aside>

            {/* Article body */}
            <article className="min-w-0 lg:col-span-9">
              <ArticleOutlineMobile items={outline} activeId={activeId} goTo={goTo} />

              <div
                data-testid="article-body-content"
                className="prose prose-lg max-w-[72ch]
                  prose-headings:font-bold prose-headings:text-stone-900 prose-headings:tracking-tight
                  prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-5 md:prose-h2:text-3xl
                  prose-h3:text-xl prose-h3:mt-9 prose-h3:mb-4 md:prose-h3:text-2xl
                  prose-h4:text-lg prose-h4:mt-7 prose-h4:mb-3
                  prose-p:text-stone-700 prose-p:leading-[1.8] prose-p:mb-6
                  prose-a:text-[#006A71] prose-a:font-medium hover:prose-a:underline
                  prose-strong:text-stone-900 prose-strong:font-semibold
                  prose-ul:my-6 prose-ol:my-6 prose-li:text-stone-700 prose-li:leading-[1.75] prose-li:my-2
                  prose-blockquote:border-l-4 prose-blockquote:border-[#006A71] prose-blockquote:bg-teal-50/40
                  prose-blockquote:px-6 prose-blockquote:py-4 prose-blockquote:my-8 prose-blockquote:not-italic
                  prose-blockquote:rounded-r-lg prose-blockquote:text-stone-700
                  prose-code:text-[#006A71] prose-code:bg-stone-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                  prose-img:rounded-xl"
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />

                {/* Harvard-Style References */}
                {article.references && article.references.length > 0 && (
                  <div className="mt-16 pt-8 border-t-2 border-gray-300">
                    <div className="flex items-center gap-3 mb-6">
                      <svg className="w-6 h-6 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                      </svg>
                      <h3 className="text-2xl font-bold text-gray-900">References</h3>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-6">
                      <ol className="space-y-4">
                        {article.references.map((ref, idx) => (
                          <li key={idx} id={`ref-${idx + 1}`} className="text-sm text-gray-700 leading-relaxed scroll-mt-24">
                            <span className="inline-block w-8 font-bold text-teal-600">{idx + 1}.</span>
                            <span className="inline">{formatHarvardReference(ref)}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                    <p className="text-xs text-gray-500 mt-4 italic">
                      * All references accessed and verified at time of publication
                    </p>
                  </div>
                )}

              {/* Still have questions? CTA */}
              <div className="bg-gradient-to-br from-teal-600 to-teal-800 rounded-xl shadow-sm p-8 md:p-10 mb-8 text-center" data-testid="article-cta">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Still have questions?</h3>
                <p className="text-white/90 mb-7 max-w-xl mx-auto leading-relaxed">
                  If anything here raised a question about your own smile, we're always happy to help — no pressure, just friendly, honest advice from our team.
                </p>
                <Link
                  to={enquireTo}
                  className="inline-flex items-center gap-2 bg-white text-teal-700 font-semibold px-8 py-3.5 rounded-full hover:bg-teal-50 hover:scale-[1.02] transition-all shadow-md"
                  data-testid="article-cta-enquire-btn"
                >
                  Enquire now
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>

              {/* Share Buttons */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Share this article</h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleShare("whatsapp")}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </button>
                  <button
                    onClick={() => handleShare("facebook")}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </button>
                  <button
                    onClick={() => handleShare("x")}
                    className="flex items-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X (Twitter)
                  </button>
                  <button
                    onClick={() => handleShare("linkedin")}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </button>
                  <button
                    onClick={() => handleShare("copy")}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                      <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                    </svg>
                    Copy Link
                  </button>
                </div>
              </div>

              {/* Related guides */}
              {related.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8" data-testid="related-guides">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Related guides</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    {related.map((rel) => {
                      const relImg = rel.heroImage || rel.hero_image;
                      return (
                        <Link
                          key={rel.slug}
                          to={`/knowledge-base/${rel.slug}`}
                          className="group border border-gray-200 rounded-lg overflow-hidden hover:border-teal-500 hover:shadow-md transition flex flex-col"
                          data-testid={`related-guide-${rel.slug}`}
                        >
                          {relImg && (
                            <img
                              src={relImg}
                              alt={rel.hero_image_alt || rel.title}
                              className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          )}
                          <div className="p-4 flex flex-col flex-1">
                            <div className="text-xs text-teal-600 font-semibold uppercase tracking-wide mb-2">
                              {rel.category}
                            </div>
                            <h4 className="font-semibold text-gray-900 group-hover:text-teal-600 transition mb-2">
                              {rel.title}
                            </h4>
                            <p className="text-sm text-gray-600 line-clamp-2">{rel.excerpt}</p>
                            <div className="mt-auto flex items-center gap-2 pt-3 text-xs font-medium text-teal-600">
                              Read guide
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </article>
          </div>
        </div>
      </div>
      <SiteFooter />
    </>
  );
};

export default ArticleDetail;
